import styled, { css, type FlattenInterpolation, type ThemedStyledProps } from 'styled-components'

import * as breakpoints from '../constants/breakpoints'
import * as colors from '../constants/colors'
import * as fonts from '../constants/fonts'
import * as typography from '../constants/neue/typography'

export type Sizes = keyof typeof styles
type AsDesktopProps = { asDesktop?: Sizes; breakpoint?: string }
export type HeadingProps = React.HTMLAttributes<HTMLDivElement> & AsDesktopProps

export const Base = styled.div.withConfig({
  shouldForwardProp: (prop) => !['asDesktop', 'breakpoint'].includes(prop),
})<HeadingProps>`
  color: ${colors.PRIMARY_GREY_900};
  font-family: ${fonts.PRIMARY_FONT};
  margin: 0;
  padding: 0;
`

const styles = {
  XXXL: typography.HEADING_3XL,
  XXL: typography.HEADING_XXL,
  XL: typography.HEADING_XL,
  L: typography.HEADING_L,
  M: typography.HEADING_M,
  S: typography.HEADING_S,
  XS: typography.HEADING_XS,
  XXS: typography.HEADING_XXS,
  XXXS: typography.HEADING_3XS,
  XXXXS: typography.HEADING_4XS,
  XXXXXS: typography.HEADING_5XS,
  XXXXXXS: typography.HEADING_6XS,
}

const asDesktopStyle: FlattenInterpolation<ThemedStyledProps<AsDesktopProps, any>> = css<AsDesktopProps>`
  @media screen and ${({ breakpoint = breakpoints.TABLET }) => breakpoint} {
    ${({ asDesktop }) => {
      return asDesktop === undefined ? '' : styles[asDesktop]
    }}
  }
`

export const XXXL = styled(Base)<HeadingProps>`
  ${typography.HEADING_3XL};
  ${asDesktopStyle};
`
XXXL.displayName = 'S.heading.XXXL'

export const XXL = styled(Base)<HeadingProps>`
  ${typography.HEADING_XXL};
  ${asDesktopStyle};
`
XXL.displayName = 'S.heading.XXL'

export const XL = styled(Base)<HeadingProps>`
  ${typography.HEADING_XL};
  ${asDesktopStyle};
`
XL.displayName = 'S.heading.XL'

export const L = styled(Base)<HeadingProps>`
  ${typography.HEADING_L};
  ${asDesktopStyle}
`
L.displayName = 'S.heading.L'

export const M = styled(Base)<HeadingProps>`
  ${typography.HEADING_M};
  ${asDesktopStyle}
`
M.displayName = 'S.heading.M'

export const S = styled(Base)<HeadingProps>`
  ${typography.HEADING_S};
  ${asDesktopStyle}
`
S.displayName = 'S.heading.S'

export const XS = styled(Base)<HeadingProps>`
  ${typography.HEADING_XS};
  ${asDesktopStyle}
`
XS.displayName = 'S.heading.XS'

export const XXS = styled(Base)<HeadingProps>`
  ${typography.HEADING_XXS};
  ${asDesktopStyle}
`
XXS.displayName = 'S.heading.XXS'

export const XXXS = styled(Base)<HeadingProps>`
  ${typography.HEADING_3XS};
  ${asDesktopStyle}
`
XXXS.displayName = 'S.heading.XXXS'

export const XXXXS = styled(Base)<HeadingProps>`
  ${typography.HEADING_4XS};
  ${asDesktopStyle}
`
XXXXS.displayName = 'S.heading.XXXXS'

export const XXXXXS = styled(Base)<HeadingProps>`
  ${typography.HEADING_5XS};
  ${asDesktopStyle}
`
XXXXXS.displayName = 'S.heading.XXXXXS'

export const XXXXXXS = styled(Base)<HeadingProps>`
  ${typography.HEADING_6XS};
  ${asDesktopStyle}
`
XXXXXXS.displayName = 'S.heading.XXXXXXS'

/**
 * HIGHLIGHT
 */

export type HighlightedTitleProps = {
  color?: 'blue' | 'green' | 'red'
  small?: boolean
}

const COLOR_PAIRING = {
  blue: [colors.PRIMARY_BLUE_100, colors.PRIMARY_BLUE_700],
  green: [colors.SECONDARY_MINT_300, colors.SECONDARY_MINT_700],
  red: [colors.SECONDARY_RED_200, colors.PRIMARY_RED_500],
}

export const HighlightedTitle = styled.span.withConfig({
  shouldForwardProp: (prop) => !['color', 'small'].includes(prop),
})<HighlightedTitleProps>`
  display: inline-block;

  ${({ color, theme }) => {
    if (color && Object.keys(COLOR_PAIRING).includes(color)) {
      return css`
        background-color: ${COLOR_PAIRING[color][0]};
        color: ${COLOR_PAIRING[color][1]};
      `
    } else if (theme?.colors?.background && theme?.colors?.primary) {
      return css`
        background-color: ${theme.colors.background};
        color: ${theme.colors.primary};
      `
    } else {
      return css`
        background-color: ${colors.PRIMARY_SAND_100};
        color: ${colors.PRIMARY_GREY_900};
      `
    }
  }}

  ${({ small }) =>
    small
      ? css`
          margin-bottom: -10px;
          padding: 4px 6px 6px;

          @media screen and ${breakpoints.LAPTOP} {
            margin-bottom: -10px;
            padding: 1px 6px 8px;
          }
        `
      : css`
          margin-bottom: -6px;
          padding: 4px 6px 6px;

          @media screen and ${breakpoints.LAPTOP} {
            margin-bottom: 0;
            padding: 1px 6px 8px;
          }
        `}
`
